import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { detect } from '../../utils/detect-browser';
import styled from 'styled-components/macro';

import Donut from '../../components/Donut';
import Logo from '../../components/Svg/ActionButtonIcon';

import { onGoToNextCard } from '../Widget/actions';

import { CardTypes } from '../../utils/enums';
import { isLocalRepresentativeAction } from '../../utils/localRepresentative/localRepresentativeActionCheck';
import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';
import { Placeholders } from '../../utils/fillDynamicText';
import { isPortrait } from '../../utils/size';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { copyFor, capitalization } from '../../copy/copy';
import countdownAnimation from '../../components/Widget/CountdownAnimation.json';
import rightArrow from '../../assets/images/right-arrow.png';
import { ThemeConsumer } from '../../utils/theming';

let lottie = {};
import('lottie-web').then(l => (lottie = l));

const Container = styled.div`
    position: absolute;
    left: 0;
    width: ${props => (props.isVisual && !isPortrait() ? 70 : 100)}%;
    cursor: pointer;
    bottom: ${props => (props.isEndCard ? 40 : -20)}px;
    ${props => props.hideFooter && 'bottom: 0;'}

    &.visible {
        transition: ${({ revealDuration }) => `bottom ${revealDuration}ms linear`};
        bottom: ${props => (props.hasNextAction && !props.hideFooter ? '40px' : '0px')};
    }
`;

const Body = styled.div`
    height: 53px;
    background-color: #ffffff;
    box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.2);
    border-bottom: 0;
    margin: 0 1em;
    padding: 0.75em 1em;
    display: flex;
    flex-direction: row;

    @media (max-width: 30em) {
        height: auto;
    }
`;

const Title = styled.div`
    height: 10px;
    font-size: 0.5em;
    font-weight: bold;
    line-height: 1.25;
    letter-spacing: 2px;
    color: #5d656c;
    text-transform: uppercase;
`;

const ActionName = styled.div`
    &::first-letter {
        text-transform: uppercase;
    }
    height: 20px;
    font-size: 0.875em;
    font-weight: 600;
    line-height: 1.43;
    margin-top: 3px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: calc(100% - 42px);

    @media (max-width: 30em) {
        font-size: 14px;
        height: auto;
    }
`;

const TextContainer = styled.div`
    width: 100%;
`;

const LogoTimer = styled.div`
    display: block;
    width: 31px;
    height: 31px;
    display: flex;
    align-items: center;
    position: absolute;
    right: 11px;
    top: 12px;
`;

const AnimationTimer = styled.div`
    visibility: visible;
    transition: visibility 0.3s;

    &.hide {
        visibility: hidden;
    }
`;

const CountdownTimerValue = styled.div`
    visibility: hidden;
    transition: visibility 0.3s;
    position: absolute;
    right: 23px;
    top: 18px;
    color: #fff;
    font-size: 14px;

    img {
        width: 6px;
        margin-top: 4px;
    }

    &.visible {
        visibility: visible !important;
    }
`;

const CountdownTimer = styled.div`
    visibility: hidden;
    transition: visibility 0.3s;
    width: 100%;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0px;

    &.visible {
        visibility: visible !important;
    }

    path {
        fill: ${({ theme }) => theme.colors.primary};
        stroke: ${({ theme }) => theme.colors.primary};
    }
`;

const CountdownWrapper = styled.div`
    width: 54px;
    height: 54px;
    position: absolute;
    right: 24px;
    top: 0px;

    > div {
        transition: visibility 0.3s;
    }

    @media (min-width: 30em) {
        ${({ noCountdown }) =>
            noCountdown
                ? `
                    > div {
                        visibility: hidden;

                        &:last-child {
                            visibility: visible !important;
                        }
                    }`
                : `
                    &:hover {
                        > div {
                            visibility: hidden;

                            &:last-child {
                                visibility: visible !important;
                            }
                        }
                    }
                `};
    }
`;

const FinalTimer = styled.div`
    visibility: ${({ noCountdown }) => (noCountdown ? `visible` : `hidden`)};
    border-radius: 100%;
    width: 32px;
    height: 32px;
    text-align: center;
    line-height: 32px;
    position: absolute;
    right: 11px;
    top: 11px;
    background: ${({ theme }) => theme.colors.primary};
`;

const RightArrow = styled.img`
    width: 7px;
    height: auto;
`;

class NextAction extends React.Component {
    static defaultProps = {
        revealDelay: 0,
        revealDuration: 500
    };

    static propTypes = {
        beforeAdvancing: PropTypes.func.isRequired,
        countdownTimer: PropTypes.number.isRequired,
        dontGoToNextCard: PropTypes.bool,
        hideFooter: PropTypes.bool,
        isEndCard: PropTypes.bool,
        isVisual: PropTypes.bool,
        nextAction: PropTypes.object,
        nextCard: PropTypes.object,
        nextCardName: PropTypes.string.isRequired,
        nextCardTitle: PropTypes.string.isRequired,
        noCountdown: PropTypes.bool,
        onGoToNextCard: PropTypes.func.isRequired,
        revealDelay: PropTypes.number,
        revealDuration: PropTypes.number,
        skipFrontCardOnNextAction: PropTypes.bool,
        sliderIsInUse: PropTypes.bool
    };

    constructor() {
        super();
        this.nextActionContainerRef = React.createRef();
        this.animationTimer = React.createRef();
        this.countdownTimer = React.createRef();
        this.countdownTimerValue = React.createRef();
        this.lottieAdded = false;
    }

    componentDidMount() {
        this.browser = detect();
        const second = 1000;
        const isEdge = this.browser.name === 'edge';
        this.timeRemaining = (this.props.countdownTimer + 2.25) * second;
        this.lottieDelay = isEdge || this.props.noCountdown ? 0 : 1.5 * second;
        this.numberDisplayDelay = isEdge || this.props.noCountdown ? 0 : 2.25 * second;
        this.paused = false;
        this.countdownAnimationStarted = false;

        if (this.props.nextAction != null) {
            this.timerStart = new Date();
            this.timeInterval = setInterval(() => this.timeProgression(100), 100);
        }

        this.checkForPauseResume({ sliderIsInUse: this.props.sliderIsInUse });
        this.tryLoadCountdownAnimations();
        const revealTimeout = setTimeout(() => {
            if (this.nextActionContainerRef.current) {
                this.nextActionContainerRef.current.classList.add('visible');
                clearTimeout(revealTimeout);
            }
        }, this.props.revealDelay);
    }

    tryLoadCountdownAnimations() {
        if (
            !this.countdownAnimationStarted &&
            this.animationTimer &&
            this.countdownTimer &&
            this.countdownTimerValue &&
            !this.paused
        ) {
            this.loadCountdownAnimations();
            this.countdownAnimationStarted = true;
        }
    }

    loadCountdownAnimations = () => {
        this.lottieTimeout = setTimeout(() => {
            this.lottieCallback();
        }, this.lottieDelay);
        this.numberDisplayInterval = setInterval(() => {
            this.intervalCallback();
        }, 1000);

        this.toggleNumberDisplayInterval = setTimeout(() => {
            if (
                this.animationTimer.current &&
                this.countdownTimer.current &&
                this.countdownTimerValue.current
            ) {
                this.animationTimer.current.classList.add('hide');
                this.countdownTimer.current.classList.add('visible');
                this.countdownTimerValue.current.classList.add('visible');
            }
        }, this.numberDisplayDelay);
    };

    lottieCallback() {
        if (
            lottie &&
            this.countdownTimer &&
            this.props.nextAction &&
            this.lottieAdded === false
        ) {
            this.lottieAdded = true;
            lottie.loadAnimation({
                container: this.countdownTimer.current,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: countdownAnimation
            });
        }
    }

    intervalCallback() {
        if (this.paused || this.props.noCountdown) return;
        if (this.timeRemaining <= 0) {
            this.nextAction();
            return;
        }
        if (this.countdownTimerValue.current) {
            const secondsRemaining = Math.floor(this.timeRemaining / 1000);
            this.countdownTimerValue.current.innerHTML =
                secondsRemaining % 2 !== 0
                    ? '<img src="' + rightArrow + '">'
                    : secondsRemaining;
        }
    }

    timeProgression(interval) {
        if (!this.paused) this.timeRemaining -= interval;
    }

    pauseTimers(shouldIncreaseTimer) {
        this.paused = true;
        const additionalTime = shouldIncreaseTimer ? 2000 : 0;
        this.timeRemaining > 8000
            ? (this.timeRemaining = 8000)
            : (this.timeRemaining += additionalTime);
        clearTimeout(this.lottieTimeout);
        this.lottieDelay -= new Date() - this.timerStart;
    }

    resumeTimers() {
        this.paused = false;
        this.tryLoadCountdownAnimations();
        clearTimeout(this.lottieTimeout);
        this.lottieTimeout = setTimeout(() => {
            this.lottieCallback();
        }, this.lottieDelay);
    }

    nextAction() {
        this.props.beforeAdvancing && this.props.beforeAdvancing();
        !this.props.dontGoToNextCard &&
        this.props.onGoToNextCard(
            this.props.nextAction.identifier,
            this.props.nextCard.type,
            this.props.nextAction.type,
            this.props.skipFrontCardOnNextAction,
            false
        );
    }

    componentWillUnmount() {
        if (this.lottieTimeout != null) {
            clearTimeout(this.lottieTimeout);
            clearInterval(this.numberDisplayInterval);
            clearInterval(this.timeInterval);
        }
    }

    componentDidUpdate(prevProps) {
        this.checkForPauseResume(prevProps);
    }

    checkForPauseResume(prevProps) {
        if (this.props.isPaused !== undefined) this.paused = this.props.isPaused
      
        const componentTurnedOff = propName =>
            prevProps[propName] === true && !this.props[propName];
        const componentTurnedOn = propName =>
            !prevProps[propName] && this.props[propName] === true;

        if (
            componentTurnedOff('isOverlayVisible') ||
            componentTurnedOff('isShareDrawerVisible') ||
            componentTurnedOff('isOverlayPublisherVisible') ||
            componentTurnedOff('isOverlayVolunteerVisible') ||
            componentTurnedOff('isOverlayPartnerVisible') ||
            componentTurnedOff('isRepStanceVisible') ||
            componentTurnedOff('isOverlayVoteSmartVisible') ||
            componentTurnedOff('isshowFindYouRepPopupVisible') ||
            componentTurnedOff('sliderIsInUse')
        ) {
            this.resumeTimers();
        } else if (
            componentTurnedOn('isOverlayVisible') ||
            componentTurnedOn('isShareDrawerVisible') ||
            componentTurnedOn('isOverlayPublisherVisible') ||
            componentTurnedOn('isOverlayVolunteerVisible') ||
            componentTurnedOn('isOverlayPartnerVisible') ||
            componentTurnedOn('isRepStanceVisible') ||
            componentTurnedOn('isOverlayVoteSmartVisible') ||
            componentTurnedOn('isshowFindYouRepPopupVisible') ||
            componentTurnedOn('sliderIsInUse')
        ) {
            this.pauseTimers(!prevProps.sliderIsInUse);
        }
    }

    render() {
        return (this.props.nextAction || this.props.dontGoToNextCard) ? (
            <ThemeConsumer>
                {theme => {
                    return (
                        <Container
                            ref={this.nextActionContainerRef}
                            isVisual={this.props.isVisual}
                            isEndCard={this.props.isEndCard}
                            revealDuration={this.props.revealDuration}
                            hideFooter={this.props.hideFooter}
                            data-container="true"
                            hasNextAction={this.props.nextAction !== null}
                            onClick={() => {
                                if (this.lottieTimeout != null) {
                                    clearTimeout(this.lottieTimeout);
                                    clearInterval(this.numberDisplayInterval);
                                    clearInterval(this.timeInterval);
                                }
                                let dataContainer = document.querySelectorAll(
                                    '[data-container]'
                                )[0];
                                dataContainer.classList.add('hide');
                                this.nextAction();
                            }}
                        >
                            <Body>
                                <TextContainer>
                                    <Title>{this.props.nextCardTitle}</Title>
                                    <ActionName
                                        dangerouslySetInnerHTML={{
                                            __html: this.props.nextCardName
                                        }}
                                    />
                                </TextContainer>
                                <CountdownWrapper noCountdown={this.props.noCountdown}>
                                    <AnimationTimer ref={this.animationTimer}>
                                        {!this.props.noCountdown && <Donut />}
                                        <LogoTimer>
                                            <Logo size="15" />
                                        </LogoTimer>
                                    </AnimationTimer>
                                    <CountdownTimer
                                        theme={theme}
                                        ref={this.countdownTimer}
                                    />
                                    {this.props.countdownTimer !== 0 && (
                                        <CountdownTimerValue
                                            ref={this.countdownTimerValue}
                                        />
                                    )}

                                    <FinalTimer
                                        theme={theme}
                                        noCountdown={this.props.noCountdown}
                                    >
                                        <RightArrow src={rightArrow} />
                                    </FinalTimer>
                                </CountdownWrapper>
                            </Body>
                        </Container>
                    );
                }}
            </ThemeConsumer>
        ) : null;
    }
}

export default connect(
    (state, props) => {
        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  state.widget.actions[props.exitingAction.order]
                      ? state.widget.actions[props.exitingAction.order]
                            .representativeSearch
                      : null,
                  state.localRep.localRepData.representative
              ).slice(0, 3)
            : [];

        const formatPlaceholders = (str, location) => {
            const i = str.indexOf(Placeholders.LocalRepresentative);
            if (reps.length > 0) {
                const repString = joinEnglish(reps.map(r => r.displayName));
                return str
                    .replace(new RegExp(Placeholders.LocalRepresentative, 'g'), repString)
                    .replace(
                        new RegExp(Placeholders.LocalePlaceholder, 'g'),
                        location || 'your area'
                    )
                    .replace(
                        new RegExp(Placeholders.StatePlaceholder, 'g'),
                        (state.localRep.localRepData || {}).stateName || 'your state'
                    )
                    .replace(
                        new RegExp(Placeholders.DistrictPlaceholder, 'g'),
                        (state.localRep.localRepData || {}).district || 'your district'
                    )
                    .replace(
                        new RegExp(Placeholders.TwitterMentionPlaceholder, 'g'),
                        joinEnglish(reps.map(rep => rep.fullName || rep.twitterMention))
                    )
                    .replace(
                        new RegExp(Placeholders.PostalCodePlaceholder, 'g'),
                        (state.localRep.localRepData.address || {}).zipCode
                    );
            } else {
                return str
                    .replace(
                        new RegExp(Placeholders.LocalRepresentative, 'g'),
                        i === 0 || str.charAt(i - 2) === '.'
                            ? copyFor(
                                  'nextAction',
                                  'yourReps',
                                  undefined,
                                  capitalization.first
                              )
                            : copyFor('nextAction', 'yourReps')
                    )
                    .replace(new RegExp(Placeholders.LocalePlaceholder, 'g'), location);
            }
        };
        const nextAction = getNextAction(
            state.widget.actions,
            props.exitingAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const nextCard = nextAction ? state.widget.cards[nextAction.order - 1] : null;
        const defaultNextCardName = copyFor('nextAction', 'defaultText');

        return {
            nextAction,
            nextCard,
            skipFrontCardOnNextAction:
                nextAction != null
                    ? nextAction.skipFrontCard ||
                      (nextAction.scrollableDesign &&
                          state.localRep.localRepData.representative !== undefined)
                    : null,
            isLocalRepAction:
                nextAction != null ? isLocalRepresentativeAction(nextAction.type) : false,
            nextCardTitle:
                (nextCard || {}).transitionHeading || copyFor('nextAction', 'title'),
            nextCardName: formatPlaceholders(
                nextAction
                    ? (nextCard || {}).heading || nextAction.name || defaultNextCardName
                    : defaultNextCardName,
                (state.localRep.localRepData || {}).stateName ||
                    (state.widget.endUserLocation || {}).regionName
            ),
            isOneActionCard: nextAction
                ? state.widget.cards[nextAction.order - 1].type === CardTypes.oneAction
                : false,
            countdownTimer: props.exitingAction.cardCountdownTimer,
            isMobileResolution: state.widget.isMobileResolution,
            isOverlayVisible: state.widget.isOverlayVisible,
            sliderIsInUse:
                state.sentimentPoll.isSubmitting || state.radialSlider.isSubmitting,
            isShareDrawerVisible: state.widget.shareDrawerVisibility,
            isOverlayPublisherVisible: state.widget.isOverlayPublisherVisible,
            isOverlayVolunteerVisible: state.volunteer.isOverlayVolunteerVisible,
            isOverlayPartnerVisible: state.widget.isOverlayPartnerVisible,
            isRepStanceVisible: state.widget.isRepStanceVisible,
            isOverlayVoteSmartVisible: state.widget.isOverlayVoteSmartVisible,
            isshowFindYouRepPopupVisible: state.localRep.showFindYouRepPopup
        };
    },
    { onGoToNextCard }
)(NextAction);
